import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogContent,
  CircularProgress,
  Snackbar,
  Alert,
  Pagination,
  Stack
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as ViewIcon, 
  Visibility as VisibilityIcon, 
  Inventory2 as ProductIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc, getDocs } from 'firebase/firestore';
import { AuthContext } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import SalesFilters from '../../components/Sales/SalesFilters';
import SaleDetails from '../../components/Sales/SaleDetails';
import ProductDetails from '../../pages/Products/ProductDetails';
import { 
  getSourceLabel, 
  getPaymentStatusLabel, 
  getDeliveryStatusLabel,
  getPaymentStatusColor,
  getDeliveryStatusColor
} from '../../constants/salesConstants';

const SalesPage = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedSale, setSelectedSale] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetailsOpen, setProductDetailsOpen] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Filtry
  const [filter, setFilter] = useState('all'); // all, in_progress, completed

  useEffect(() => {
    fetchSales();
  }, [currentUser, filter]);



  const fetchSales = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const salesCollection = collection(db, 'sales');
      let salesQuery;

      if (isAdmin) {
        salesQuery = query(salesCollection, orderBy('transaction_date', 'desc'));
      } else {
        salesQuery = query(salesCollection, where('employee_id', '==', currentUser.uid));
      }

      const salesSnapshot = await getDocs(salesQuery);

      const salesListPromises = salesSnapshot.docs.map(async (saleDoc) => {
        const saleData = saleDoc.data();
        const newSale = {
          id: saleDoc.id,
          ...saleData,
          transaction_date: saleData.transaction_date?.toDate(),
          completedAt: saleData.completedAt?.toDate(),
        };

        // Równoległe pobieranie danych dodatkowych
        const productPromise = saleData.product_id ? getDoc(doc(db, 'products', saleData.product_id)) : Promise.resolve(null);
        const clientPromise = saleData.client_id ? getDoc(doc(db, 'clients', saleData.client_id)) : Promise.resolve(null);
        const employeePromise = saleData.employee_id ? getDoc(doc(db, 'users', saleData.employee_id)) : Promise.resolve(null);

        const [productSnap, clientSnap, employeeSnap] = await Promise.all([productPromise, clientPromise, employeePromise]);

        if (productSnap && productSnap.exists()) {
          newSale.productDetails = { id: productSnap.id, ...productSnap.data() };
        }

        if (clientSnap && clientSnap.exists()) {
          const clientData = clientSnap.data();
          newSale.clientName = `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim();
        } else {
          newSale.clientName = 'Nieznany Klient';
        }

        if (employeeSnap && employeeSnap.exists()) {
          const employeeData = employeeSnap.data();
          newSale.employeeName = `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim();
        } else {
          newSale.employeeName = 'Nieznany Sprzedawca';
        }

        return newSale;
      });

      const salesList = await Promise.all(salesListPromises);

      // Filtrowanie po statusie
      const filteredSales = salesList.filter(sale => {
        if (filter === 'all') return true;
        if (filter === 'in_progress') return sale.status !== 'completed';
        if (filter === 'completed') return sale.status === 'completed';
        return true;
      });

      setSales(filteredSales);

    } catch (error) {
      console.error("Błąd podczas pobierania sprzedaży: ", error);
      setSnackbar({ open: true, message: 'Wystąpił błąd podczas pobierania danych sprzedaży.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewSale = (sale) => {
    setSelectedSale(sale);
    setDetailsOpen(true);
  };
  
  const handleViewProduct = async (e, productId) => {
    if (e) e.stopPropagation(); // Zapobiegaj propagacji zdarzenia kliknięcia, jeśli istnieje
    try {
      const productRef = doc(db, 'products', productId);
      const productSnap = await getDoc(productRef);
      
      if (productSnap.exists()) {
        setSelectedProduct({
          id: productId,
          ...productSnap.data()
        });
        setProductDetailsOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: 'Nie można znaleźć produktu. Mógł zostać usunięty z bazy danych.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Błąd podczas pobierania danych produktu:', error);
      setSnackbar({
        open: true,
        message: 'Wystąpił błąd podczas pobierania danych produktu',
        severity: 'error'
      });
    }
  };

  const handleSaleUpdate = (updatedSale) => {
    setSales(sales.map(sale => 
      sale.id === updatedSale.id ? updatedSale : sale
    ));
    setSelectedSale(updatedSale);
    setSnackbar({
      open: true,
      message: 'Sprzedaż została zaktualizowana',
      severity: 'success'
    });
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const formatDate = (date) => {
    if (!date) return 'Brak daty';
    return format(date instanceof Date ? date : date.toDate(), 'dd.MM.yyyy', { locale: pl });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };



  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Sprzedaże
      </Typography>

      <SalesFilters 
        filter={filter}
        onFilterChange={handleFilterChange}
      />

      {/* Tabela sprzedaży */}
      <Box sx={{ overflowX: 'auto' }}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 1200 }} aria-label="Tabela sprzedaży">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Produkt</TableCell>
                <TableCell>Data transakcji</TableCell>
                <TableCell>Klient</TableCell>
                {isAdmin && <TableCell>Pracownik</TableCell>}
                <TableCell>Źródło</TableCell>
                <TableCell>Wartość</TableCell>
                <TableCell>Status płatności</TableCell>
                <TableCell>Status dostawy</TableCell>
                <TableCell>Zadatek</TableCell>
                <TableCell>Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 11 : 10} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : sales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 11 : 10} align="center">
                    <Typography variant="body1" color="textSecondary">
                      Brak sprzedaży spełniających kryteria
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sales.map((sale) => (
                  <TableRow key={sale.id} hover>
                    <TableCell>{sale.id.substring(0, 8)}...</TableCell>
                    <TableCell>
                      {sale.productDetails ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {sale.productDetails.images && sale.productDetails.images.length > 0 ? (
                            <img 
                              src={sale.productDetails.images[0]}
                              alt={sale.productDetails.name || 'Produkt'}
                              style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: '4px' }} 
                            />
                          ) : (
                            <Box sx={{ width: 50, height: 50, backgroundColor: '#333', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography variant="caption" color="text.secondary">Brak foto</Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography variant="body2" component="div" noWrap sx={{ fontWeight: '500' }}>
                              {sale.productDetails.name || sale.productDetails.jewelry_name || 'Brak nazwy'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {sale.productDetails.sku || 'Brak SKU'}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Brak danych produktu</Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(sale.transaction_date)}</TableCell>
                    <TableCell>
                      {sale.clientName}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>{sale.employeeName}</TableCell>
                    )}
                    <TableCell>
                      {getSourceLabel(sale.source)}
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatPrice(sale.amount)}</strong>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getPaymentStatusLabel(sale.payment_status || 'unpaid')} 
                        color={getPaymentStatusColor(sale.payment_status || 'unpaid')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getDeliveryStatusLabel(sale.delivery_status || 'pending')} 
                        color={getDeliveryStatusColor(sale.delivery_status || 'pending')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {sale.deposit ? (
                        <Chip 
                          label={formatPrice(sale.deposit.amount)} 
                          color="primary"
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Brak
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {sale.productId && (
                          <Tooltip title="Zobacz szczegóły produktu">
                            <IconButton 
                              size="small"
                              onClick={(e) => handleViewProduct(e, sale.productId)}
                              color="primary"
                            >
                              <ProductIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Zobacz szczegóły sprzedaży">
                          <IconButton onClick={() => handleViewSale(sale)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {sale.invoiceNumber && (
                          <Tooltip title="Faktura">
                            <IconButton 
                              size="small"
                              onClick={() => console.log('Pobierz fakturę', sale.invoiceNumber)}
                            >
                              <ReceiptIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Dialog szczegółów sprzedaży */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        {selectedSale && (
          <SaleDetails
            sale={selectedSale}
            onClose={() => setDetailsOpen(false)}
            onUpdate={handleSaleUpdate}
            onViewProduct={(productId) => {
              setDetailsOpen(false); // Zamknij modal sprzedaży
              // Użyj setTimeout, aby uniknąć konfliktów renderowania modali
              setTimeout(() => {
                handleViewProduct(null, productId); // Otwórz modal produktu
              }, 150);
            }}
          />
        )}
      </Dialog>

      {/* Dialog szczegółów produktu */}
      <Dialog
        open={productDetailsOpen}
        onClose={() => setProductDetailsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        {selectedProduct && (
          <ProductDetails
            product={selectedProduct}
            open={productDetailsOpen} // Przekazanie stanu otwarcia
            onClose={() => setProductDetailsOpen(false)}
            isModal={true}
            readOnly={true}
            fromSalesHistory={true}
          />
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SalesPage;
